import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetAcceptancesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  useGetAcceptancesRegister,
  type UseGetAcceptancesRegisterArgs,
} from 'src/hooks/queries/acceptance/useGetAcceptancesRegister';

import { acceptanceRegisterUrl } from '@/utils/urls';

import { useGetAcceptanceSmartWidgetTableProps } from '../../../acceptances/config';
import type { AcceptanceTableFields } from '../../../acceptances/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import type { DashboardFilter } from '../../useDashboardStore';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilterWithUnrated } from '../dataSourceHelpers';

export default createDataSource<
  AcceptanceTableFields,
  UseGetAcceptancesRegisterArgs,
  GetAcceptancesQuery
>({
  group: 'single',
  hasAccess: () => true,
  useWidgetQuery: useGetAcceptancesRegister,
  parentTypes: [Parent_Type_Enum.Acceptance],
  useTablePropsHook: (data, options) =>
    useGetAcceptanceSmartWidgetTableProps(data?.acceptance, options),
  entityNamePlural: 'acceptance_other',
  entityNameSingular: 'acceptance_one',
  fields: 'acceptances.fields',
  clickThroughUrl: (filters) => acceptanceRegisterUrl(filters),
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ tagTypeIds: tags }),
    departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
    dateFilter: (dateRange, precision) => ({
      dateRange: {
        field: 'DateAccepted',
        ...toIsoBounds(dateRange, precision),
      },
    }),
    ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
    dateClickthroughFilter: (filter: DashboardFilter['dateRange']) => {
      // Via `toIsoBounds`, not `convertDateRangeValues`: these bounds hit
      // `timestamp` columns holding date-only values stored at UTC midnight (CUS-318).
      const { from, to } = toIsoBounds(filter);

      return from && to
        ? [
            {
              propertyKey: 'DateAcceptedFrom',
              operator: '>=',
              value: from,
            } as const,
            {
              propertyKey: 'DateAcceptedTo',
              operator: '<=',
              value: to,
            } as const,
          ]
        : [];
    },
  },
  categoryGetters: [
    {
      id: 'status',
      fieldId: 'Status',
      name: () => i18n.t('acceptances.columns.status'),
      categoryGetter: (item) => ({
        key: item.Status,
        label: item.StatusLabelled,
      }),
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('StatusLabelled'),
      ratingColourKey: 'acceptance_status',
    },
  ],
});
